import { worldRoot } from './worldRoot'
/**
 * PlayCanvas server room — daemon obelisk with health-colored blink,
 * DOM uptime label, robot character, and Kenney cabinet-desk dressing.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { DaemonHealth, DaemonPluginHealth, DaemonPluginStatus } from '../../api/types'
import { spawnCharacter } from '../characters'
import type { CharacterHandle } from '../characters'
import { setupServerRoomDressing } from './dressing'
import type { ServerRoomDressingHandles } from './dressing'
import {
  SERVER_OBELISK_POS, SERVER_ROBOT_POS, SERVER_ROOM_LIGHT_POS,
  serverIndicatorLevel,
} from './layout'
import { daemonStatusView } from './statusViewModels'

// LAYOUT-1: the server room is the enclosed NW room (x ∈ [-17,-12], z ∈ [-11,-7]).
const OBELISK_X = SERVER_OBELISK_POS[0]
const OBELISK_Z = SERVER_OBELISK_POS[1]

// Robot attendant works at the cabinet desk inside the room
const ROBOT_X = SERVER_ROBOT_POS[0]
const ROBOT_Z = SERVER_ROBOT_POS[1]

const PLUGIN_SEVERITY: Record<DaemonPluginStatus, number> = {
  down: 0,
  degraded: 1,
  unknown: 2,
  healthy: 3,
}

const RACK_STATUS_COLOR: Record<DaemonPluginStatus, readonly [number, number, number]> = {
  healthy: [0.21, 0.75, 0.48],
  degraded: [0.88, 0.66, 0.23],
  down: [0.84, 0.27, 0.27],
  unknown: [0.18, 0.36, 0.54],
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0
}

/** Deterministic physical-slot projection: unhealthy first, then kind/name. */
export function orderedRackPlugins(
  plugins: readonly DaemonPluginHealth[] | undefined,
): readonly DaemonPluginHealth[] {
  return [...(plugins ?? [])]
    .sort((left, right) =>
      PLUGIN_SEVERITY[left.status] - PLUGIN_SEVERITY[right.status] ||
      compareText(left.kind, right.kind) ||
      compareText(left.name, right.name),
    )
    .slice(0, 6)
}

export class ServerRoomSystem {
  private app!: pc.Application
  private orbMat!: pc.StandardMaterial
  private pillarMat!: pc.StandardMaterial
  private elapsed = 0
  private currentHealth: DaemonHealth | null = null
  private uptimeDiv: HTMLDivElement | null = null
  private unsubscribe?: () => void
  private robotHandle: CharacterHandle | null = null
  private dressingEntities: pc.Entity[] = []
  private indicatorMats: pc.StandardMaterial[] = []
  private windowFillLight: pc.Entity | null = null

  init(
    app: pc.Application,
    windowFillLight?: pc.Entity,
    options: { externalProjection?: boolean } = {},
  ): void {
    this.app = app
    this.windowFillLight = windowFillLight ?? null

    this.buildObelisk()
    this.buildUptimeLabel()

    // Kenney prop dressing (cabinet, indicators, laptop, etc.)
    const dressing: ServerRoomDressingHandles = setupServerRoomDressing(app)
    this.dressingEntities = dressing.entities
    this.indicatorMats = dressing.indicatorMats

    // Robot character in Robot_Sitting pose at the cabinet desk
    this.robotHandle = spawnCharacter(app, 'char_robot.glb', [ROBOT_X, 0, ROBOT_Z])
    this.robotHandle.setAnimState('working') // maps to RobotArmature|Robot_Sitting

    // Slightly bluer local point light for the server room
    const roomLight = new pc.Entity('serverRoomLight')
    roomLight.addComponent('light', {
      type: 'point',
      color: new pc.Color(0.72, 0.80, 1.0),
      intensity: 0.9,
      range: 7,
      castShadows: false,
    })
    roomLight.setLocalPosition(SERVER_ROOM_LIGHT_POS[0], SERVER_ROOM_LIGHT_POS[1], SERVER_ROOM_LIGHT_POS[2])
    worldRoot(app).addChild(roomLight)
    this.dressingEntities.push(roomLight)

    if (!options.externalProjection) this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (state.health !== prev.health) {
        this.projectHealth(state.health)
      }
    })

    if (!options.externalProjection) this.projectHealth(useArenaStore.getState().health)
  }

  /** Pulsing orb + indicator blink animation — call each frame. */
  frame(dt: number): void {
    this.elapsed += dt
    const speed = daemonStatusView(this.currentHealth).pulseSpeed
    const intensity = 0.6 + 0.4 * Math.abs(Math.sin(this.elapsed * speed))
    this.orbMat.emissiveIntensity = intensity
    this.orbMat.update()

    // Blink cabinet indicators irregularly — seeded per-index step function
    // (server racks never pulse in a smooth sine).
    for (let i = 0; i < this.indicatorMats.length; i++) {
      this.indicatorMats[i].emissiveIntensity = serverIndicatorLevel(i, this.elapsed)
      this.indicatorMats[i].update()
    }
  }

  private buildObelisk(): void {
    const color = daemonStatusView(null).rgb

    // Pillar (sized to fit under the 4 m office ceiling)
    const pillar = new pc.Entity('daemonPillar')
    pillar.addComponent('render', { type: 'box' })
    pillar.setLocalScale(0.6, 2.6, 0.6)
    pillar.setLocalPosition(OBELISK_X, 1.3, OBELISK_Z)

    this.pillarMat = new pc.StandardMaterial()
    this.pillarMat.diffuse = new pc.Color(0.06, 0.09, 0.1)
    this.pillarMat.emissive = new pc.Color(color[0] * 0.05, color[1] * 0.05, color[2] * 0.05)
    this.pillarMat.metalness = 0.8
    this.pillarMat.gloss = 0.7
    this.pillarMat.update()
    if (pillar.render) {
      for (const mi of pillar.render.meshInstances) mi.material = this.pillarMat
    }
    worldRoot(this.app).addChild(pillar)

    // Cap
    const cap = new pc.Entity('daemonCap')
    cap.addComponent('render', { type: 'cone' })
    cap.setLocalScale(0.7, 0.5, 0.7)
    cap.setLocalPosition(OBELISK_X, 2.85, OBELISK_Z)

    const capMat = new pc.StandardMaterial()
    capMat.diffuse = new pc.Color(color[0], color[1], color[2])
    capMat.emissive = new pc.Color(color[0] * 0.3, color[1] * 0.3, color[2] * 0.3)
    capMat.metalness = 0.9
    capMat.gloss = 0.8
    capMat.update()
    if (cap.render) {
      for (const mi of cap.render.meshInstances) mi.material = capMat
    }
    worldRoot(this.app).addChild(cap)

    // Health orb
    const orb = new pc.Entity('daemonOrb')
    orb.addComponent('render', { type: 'sphere' })
    orb.setLocalScale(0.5, 0.5, 0.5)
    orb.setLocalPosition(OBELISK_X, 2.8, OBELISK_Z)

    this.orbMat = new pc.StandardMaterial()
    this.orbMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissive = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissiveIntensity = 0.8
    this.orbMat.metalness = 0.3
    this.orbMat.gloss = 0.9
    this.orbMat.update()
    if (orb.render) {
      for (const mi of orb.render.meshInstances) mi.material = this.orbMat
    }
    worldRoot(this.app).addChild(orb)
  }

  private buildUptimeLabel(): void {
    const gd = this.app.graphicsDevice as { canvas?: HTMLCanvasElement }
    const parent = gd.canvas?.parentElement
    if (!parent) return

    this.uptimeDiv = document.createElement('div')
    Object.assign(this.uptimeDiv.style, {
      position:      'absolute',
      bottom:        '1rem',
      right:         '1rem',
      fontFamily:    'ui-monospace, monospace',
      fontSize:      '11px',
      color:         '#475569',
      pointerEvents: 'none',
      zIndex:        '5',
    })
    this.uptimeDiv.textContent = 'DAEMON · OFFLINE'
    parent.appendChild(this.uptimeDiv)
  }

  /** P7 seam: drive racks and obelisk from the coordinator's freshness gate. */
  projectHealth(health: DaemonHealth | null): void {
    this.currentHealth = health
    const view = daemonStatusView(health, { includePool: false })
    const color = health ? view.rgb : [0.18, 0.36, 0.54] as const

    this.orbMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissive = new pc.Color(color[0], color[1], color[2])
    this.orbMat.update()

    this.pillarMat.emissive = new pc.Color(color[0] * 0.05, color[1] * 0.05, color[2] * 0.05)
    this.pillarMat.update()

    const plugins = orderedRackPlugins(health?.plugins)
    for (let index = 0; index < this.indicatorMats.length; index++) {
      const rackColor = RACK_STATUS_COLOR[plugins[index]?.status ?? 'unknown']
      this.indicatorMats[index].emissive = new pc.Color(...rackColor)
      this.indicatorMats[index].update()
    }

    // Tint the window fill light to reflect daemon health
    const lc = this.windowFillLight?.light as { color?: pc.Color } | undefined
    if (lc?.color) {
      // Blend health colour into a warm-white base
      lc.color.r = color[0] * 0.15 + 0.75
      lc.color.g = color[1] * 0.12 + 0.76
      lc.color.b = color[2] * 0.10 + 0.68
    }

    if (this.uptimeDiv) {
      this.uptimeDiv.style.color = view.cssColor
      this.uptimeDiv.textContent = view.label
    }
  }

  destroy(): void {
    this.unsubscribe?.()
    this.uptimeDiv?.remove()
    this.robotHandle?.destroy()
    this.robotHandle = null
    for (const e of this.dressingEntities) e.destroy()
    this.dressingEntities = []
    this.indicatorMats = []
  }
}
